'use client'

import { useState, useMemo, useCallback } from 'react'

export interface PriceRange {
  minCents: number | null
  maxCents: number | null
}

export interface SpecFilter {
  field: string
  operator: 'eq' | 'in' | 'gte' | 'lte'
  value: string | number | boolean | string[]
}

export interface FilterState {
  priceRange: PriceRange
  setPriceRange: (range: PriceRange) => void
  conditions: string[]
  setConditions: (conditions: string[]) => void
  specFilters: SpecFilter[]
  setSpecFilters: (filters: SpecFilter[]) => void
  hasActiveFilters: boolean
  activeFilterCount: number
  resetFilters: () => void
}

const DEFAULT_PRICE_RANGE: PriceRange = { minCents: null, maxCents: null }

export function useFilterState(): FilterState {
  const [priceRange, setPriceRange] = useState<PriceRange>(DEFAULT_PRICE_RANGE)
  const [conditions, setConditions] = useState<string[]>([])
  const [specFilters, setSpecFilters] = useState<SpecFilter[]>([])

  const hasActiveFilters = useMemo(
    () =>
      priceRange.minCents !== null ||
      priceRange.maxCents !== null ||
      conditions.length > 0 ||
      specFilters.length > 0,
    [priceRange, conditions, specFilters]
  )

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (priceRange.minCents !== null || priceRange.maxCents !== null) count++
    count += conditions.length
    count += specFilters.length
    return count
  }, [priceRange, conditions, specFilters])

  const resetFilters = useCallback(() => {
    setPriceRange(DEFAULT_PRICE_RANGE)
    setConditions([])
    setSpecFilters([])
  }, [])

  return {
    priceRange,
    setPriceRange,
    conditions,
    setConditions,
    specFilters,
    setSpecFilters,
    hasActiveFilters,
    activeFilterCount,
    resetFilters,
  }
}
